import { h, Component, Host, State, Element, Prop, Watch, Listen } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { CaptionSchemeDefinition, CaptionSchemeName } from "./constants/caption-types";
import { CAPTION_SCHEME_CONFIG, CAPTION_SCHEME_MAP } from "./constants/caption-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { isMobile, isTablet, isDesktopLarge } from "../../utils/screen-helper";
import { CaptionSchemeConfiguration } from "../../constants/colors";

@Component({
  tag: "eon-ui-caption",
  styleUrl: "caption.scss",
  shadow: true
})
export class Caption implements ColorableComponent<CaptionSchemeConfiguration<CaptionSchemeDefinition>> {
  schemeConfig: CaptionSchemeConfiguration<CaptionSchemeDefinition> = CAPTION_SCHEME_CONFIG;
  schemeMap = CAPTION_SCHEME_MAP;

  @State() isMobile: boolean;
  @State() isTablet: boolean;
  @State() isDesktopLarge: boolean;
  @State() copyrightVerticalPlacement: boolean;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: CaptionSchemeDefinition;

  @Element() hostElement: HTMLEonUiCaptionElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: CaptionSchemeName;

  /**
   * Caption content
   */
  @Prop({ reflect: true }) caption: string;

  /**
   * copyright content
   */
  @Prop({ reflect: true }) copyright: string;

  /**
   * verticalBreakpointWidth gives editor flexibility to define maximum breakpoint width for desktop viewport
   * So that if caption component width is less than verticalBreakpointWidth then content inside component will be placed vertically aligned
   * @example: default value: 450 | Can be 600 | 700
   */
  @Prop({ reflect: true }) verticalBreakpointWidth: number = 450;

  componentWillLoad() {
    this.applyScheme();
    this.windowOnResize();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.windowOnResize();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Listen("resize", { target: "window" })
  windowOnResize() {
    this.isMobile = isMobile();
    this.isTablet = isTablet();
    this.isDesktopLarge = isDesktopLarge();
    this.handleCaptionPlacement();
  }

  @Watch("verticalBreakpointWidth")
  handleCaptionPlacement() {
    const maxWidthOfImage = parseInt(window.getComputedStyle(this.hostElement).width);
    if (maxWidthOfImage <= this.verticalBreakpointWidth) {
      this.copyrightVerticalPlacement = true;
    } else {
      this.copyrightVerticalPlacement = false;
    }
  }

  render() {
    return (
      <Host>
        {(this.caption || this.copyright) && (
          <div
            class={pfx({
              ["caption-copyright-wrapper"]: true,
              ["vertical"]: this.copyrightVerticalPlacement,
              ["no-caption"]: !this.caption
            })}
          >
            {this.caption && (
              <div
                class={pfx({
                  ["caption-content"]: true,
                  ["no-copyright"]: !this.copyright
                })}
              >
                <eon-ui-text text-style="copy-small" font-weight="500">
                  {this.caption}
                </eon-ui-text>
              </div>
            )}
            {this.copyright && (
              <div class={pfx("copyright-content")}>
                <eon-ui-text text-style="copy-small">{this.copyright}</eon-ui-text>
              </div>
            )}
          </div>
        )}
      </Host>
    );
  }
}
